import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  fetchConvertTrades,
  fetchFiatDeposits,
  fetchFiatWithdrawals,
  formatAedAmount,
} from "../../../api/services/fiatService";
import { convertDeepLink, formatQuoteAmount } from "../BuySellPage/convertCatalog";

const PAGE_SIZE = 20;

const TYPE_FILTERS = [
  { value: "all", label: "All" },
  { value: "DEPOSIT", label: "Deposits" },
  { value: "CONVERT_BUY", label: "Buy" },
  { value: "CONVERT_SELL", label: "Sell" },
  { value: "AED_WITHDRAW", label: "Withdrawals" },
];

const DEPOSIT_STATUS = {
  COMPLETED: "Completed",
  LIMIT_HOLD: "In review",
  REJECTED_HOLD: "Not accepted",
  REFUND_PENDING: "Refund pending",
  REFUNDED: "Refunded",
  REVERSED: "Reversed",
  FAILED: "Failed",
};

const CONVERT_STATUS = {
  EXECUTED: "Executed",
  QUOTED: "Quoted",
  EXPIRED: "Expired",
  FAILED: "Failed",
};

function formatHistDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

function depositTone(status) {
  if (status === "COMPLETED") return "success";
  if (status === "LIMIT_HOLD" || status === "REFUND_PENDING") return "pending";
  if (status === "REFUNDED") return "info";
  if (status === "REJECTED_HOLD" || status === "REVERSED" || status === "FAILED") return "danger";
  return "neutral";
}

function convertTone(status) {
  if (status === "EXECUTED") return "success";
  if (status === "QUOTED") return "pending";
  if (status === "FAILED") return "danger";
  return "neutral";
}

function withdrawTone(status) {
  if (status === "COMPLETED" || status === "PROCESSED") return "success";
  if (status === "AWAITING_ADMIN" || status === "SUBMITTED" || status === "INITIATED") return "pending";
  if (status === "REJECTED" || status === "FAILED" || status === "REVERSED") return "danger";
  return "neutral";
}

function last4Label(last4, masked) {
  if (masked) return masked;
  const s = String(last4 || "").trim();
  return s ? `•••• ${s}` : "—";
}

function ordinal(n) {
  const v = Number.parseInt(String(n), 10);
  if (!Number.isFinite(v) || v < 1) return "";
  const m = v % 100;
  const suffix = ["th", "st", "nd", "rd"][(m - 20) % 10] || ["th", "st", "nd", "rd"][m] || "th";
  return `${v}${suffix}`;
}

function feePositive(value) {
  const n = Number.parseFloat(String(value ?? "0"));
  return Number.isFinite(n) && n > 0;
}

function isBelowMinHold(item) {
  const status = String(item?.status || "").toUpperCase();
  if (status !== "LIMIT_HOLD") return false;
  const reason = String(item?.status_reason || "").toLowerCase();
  return /min|minimum|below/.test(reason);
}

function mapDeposit(item) {
  const status = String(item?.status || "").toUpperCase();
  const currency = item.currency || "AED";
  const belowMinHold = isBelowMinHold(item);
  const fee = item.fee_amount || "0";
  const credited = item.net_credited || item.amount || "0";
  const hasFee = feePositive(fee);
  const gross = item.gross_amount || (!hasFee ? credited : "");
  const seq = item.deposit_seq_in_day;
  const seqLabel = hasFee && seq != null && seq !== "" ? ` · ${ordinal(seq)} deposit today` : "";
  const parts = [
    gross ? `Gross ${formatAedAmount(gross)}` : null,
    `Fee ${formatAedAmount(fee)}`,
    last4Label(item.debtor_iban_last4),
  ].filter(Boolean);
  return {
    key: `dep:${item.id}`,
    type: "DEPOSIT",
    typeLabel: "Deposit",
    createdAt: item.credited_at || item.created_at || "",
    dateLabel: formatHistDate(item.credited_at || item.created_at),
    detail: `${parts.join(" · ")}${seqLabel}`,
    amountLabel: belowMinHold ? "Not credited" : `${formatAedAmount(credited)} ${currency}`,
    status,
    statusLabel: belowMinHold ? "Below minimum" : DEPOSIT_STATUS[status] || status || "—",
    statusTone: depositTone(status),
    statusReason: belowMinHold
      ? `${item.status_reason || "Below the deposit minimum."} Wait for an admin refund. Do not retry KYC.`
      : item.status_reason || "",
    href: "/asset_managemnet/deposit_fiat",
    actionLabel: "View deposits",
  };
}

function mapTrade(item) {
  const status = String(item?.status || "").toUpperCase();
  const side = String(item?.side || "").toUpperCase();
  const isSell = side === "SELL";
  const spendCode = isSell ? item.base_asset : item.quote_asset;
  const receiveCode = isSell ? item.quote_asset : item.base_asset;
  const spend = item.you_spend || (isSell ? item.amount_crypto : item.amount_aed) || "0";
  const receive = item.you_receive || (isSell ? item.amount_aed : item.amount_crypto) || "0";
  const asset = String(item.base_asset || "USDT").toUpperCase();
  return {
    key: `cvt:${item.id || item.quote_id}`,
    type: isSell ? "CONVERT_SELL" : "CONVERT_BUY",
    typeLabel: isSell ? "Sell" : "Buy",
    createdAt: item.executed_at || item.created_at || "",
    dateLabel: formatHistDate(item.executed_at || item.created_at),
    detail: `${formatQuoteAmount(spend, spendCode === "AED" ? 2 : 6)} ${spendCode || "—"} → ${formatQuoteAmount(receive, receiveCode === "AED" ? 2 : 6)} ${receiveCode || "—"}`,
    amountLabel: isSell
      ? `${formatAedAmount(receive)} AED`
      : `${formatQuoteAmount(receive, 6)} ${asset}`,
    status,
    statusLabel: CONVERT_STATUS[status] || status || "—",
    statusTone: convertTone(status),
    statusReason: "",
    href: convertDeepLink(isSell ? "sell" : "buy", asset),
    actionLabel: isSell ? `Sell ${asset}` : `Buy ${asset}`,
  };
}

function mapWithdraw(item) {
  const status = String(item?.status || "").toUpperCase();
  const ben = item?.whitelist || item?.beneficiary || null;
  return {
    key: `wd:${item.id}`,
    type: "AED_WITHDRAW",
    typeLabel: "Withdraw",
    createdAt: item.created_at || "",
    dateLabel: formatHistDate(item.created_at),
    detail: last4Label(ben?.iban_last4 || item?.iban_last4, ben?.iban_masked || item?.iban_masked),
    amountLabel: `${formatAedAmount(item.amount)} AED`,
    feeLabel: `${formatAedAmount(item.fee_aed || "0")} AED`,
    netLabel: `${formatAedAmount(item.net_aed || item.amount)} AED`,
    status,
    statusLabel: item.status_label || status || "—",
    statusTone: withdrawTone(status),
    statusReason: item.status_reason || "",
    href: "/asset_managemnet/withdraw_fiat",
    actionLabel: "View withdrawals",
  };
}

function sortByDateDesc(a, b) {
  const ta = new Date(a.createdAt).getTime();
  const tb = new Date(b.createdAt).getTime();
  const na = Number.isFinite(ta) ? ta : 0;
  const nb = Number.isFinite(tb) ? tb : 0;
  return nb - na;
}

function emptyCopy(typeFilter) {
  if (typeFilter === "DEPOSIT") return "No bank deposits yet. Transfer AED to your virtual IBAN.";
  if (typeFilter === "CONVERT_BUY") return "No buys yet. Open Buy Crypto to get a live quote.";
  if (typeFilter === "CONVERT_SELL") return "No sells yet. Open Buy Crypto to get a live quote.";
  if (typeFilter === "AED_WITHDRAW") return "No AED withdrawals yet.";
  return "No fiat activity yet. Deposit AED, then buy USDT or withdraw to your bank.";
}

export default function FiatActivityList({ variant = "page" }) {
  const [typeFilter, setTypeFilter] = useState("all");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  const loadPage = useCallback(
    async (nextPage, replace) => {
      if (replace) {
        setLoading(true);
        setError(null);
      } else {
        setLoadingMore(true);
      }

      const params = { page: nextPage, limit: PAGE_SIZE };
      let mapped = [];
      let more = false;
      let failed = null;

      if (typeFilter === "all") {
        const [dep, trades, wds] = await Promise.all([
          fetchFiatDeposits(params),
          fetchConvertTrades({ ...params, status: "EXECUTED" }),
          fetchFiatWithdrawals(params),
        ]);
        const parts = [];
        if (dep.ok) parts.push(...(dep.items || []).map(mapDeposit));
        else failed = dep.error?.message;
        if (trades.ok) parts.push(...(trades.items || []).map(mapTrade));
        else failed = failed || trades.error?.message;
        if (wds.ok) parts.push(...(wds.items || []).map(mapWithdraw));
        else failed = failed || wds.error?.message;
        mapped = parts.sort(sortByDateDesc);
        more = !!(dep.meta?.has_more || trades.meta?.has_more || wds.meta?.has_more);
        if (!dep.ok && !trades.ok && !wds.ok) {
          mapped = [];
          failed = failed || "Could not load activity.";
        } else {
          failed = null;
        }
      } else if (typeFilter === "DEPOSIT") {
        const res = await fetchFiatDeposits(params);
        if (!res.ok) failed = res.error?.message || "Could not load deposits.";
        else {
          mapped = (res.items || []).map(mapDeposit);
          more = !!res.meta?.has_more;
        }
      } else if (typeFilter === "CONVERT_BUY" || typeFilter === "CONVERT_SELL") {
        const side = typeFilter === "CONVERT_SELL" ? "SELL" : "BUY";
        const res = await fetchConvertTrades({ ...params, side, status: "EXECUTED" });
        if (!res.ok) failed = res.error?.message || "Could not load converts.";
        else {
          mapped = (res.items || []).map(mapTrade);
          more = !!res.meta?.has_more;
        }
      } else {
        const res = await fetchFiatWithdrawals(params);
        if (!res.ok) failed = res.error?.message || "Could not load withdrawals.";
        else {
          mapped = (res.items || []).map(mapWithdraw);
          more = !!res.meta?.has_more;
        }
      }

      if (failed && replace) {
        setRows([]);
        setError(failed);
        setHasMore(false);
        setLoading(false);
        setLoadingMore(false);
        return;
      }

      setRows((prev) => {
        const next = replace ? mapped : [...prev, ...mapped];
        return typeFilter === "all" ? next.slice().sort(sortByDateDesc) : next;
      });
      setPage(nextPage);
      setHasMore(more);
      setError(null);
      setLoading(false);
      setLoadingMore(false);
    },
    [typeFilter]
  );

  useEffect(() => {
    loadPage(1, true);
  }, [loadPage]);

  const titleId = variant === "orders" ? "fiat-activity-orders-title" : "fiat-activity-list-title";

  return (
    <section className="df-deposit-history-section fiat-activity-list" aria-labelledby={titleId}>
      <div className={variant === "orders" ? undefined : "container"}>
        <div className="df-deposit-history-top">
          <h2 id={titleId} className="df-deposit-history-title">
            {variant === "orders" ? "History" : "Fiat activity"}
          </h2>
          <div className="df-deposit-history-filters" role="group" aria-label="Filter fiat activity">
            {TYPE_FILTERS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className={`fiat-activity-chip${typeFilter === opt.value ? " is-active" : ""}`}
                onClick={() => setTypeFilter(opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div className="df-deposit-history-card">
          <div className="df-deposit-history-table-wrap" role="region" aria-label="Fiat activity table">
            {error ? (
              <div className="df-deposit-history-status" role="status">
                <p>{error}</p>
                <button type="button" className="df-deposit-history-export-btn" onClick={() => loadPage(1, true)}>
                  Try again
                </button>
              </div>
            ) : null}

            {loading && rows.length === 0 && !error ? (
              <p className="df-deposit-history-status">Loading activity…</p>
            ) : null}

            {!loading && rows.length === 0 && !error ? (
              <div className="df-deposit-history-empty" aria-live="polite">
                <img src="/images/no-data.svg" alt="" className="df-deposit-history-empty-img" width={120} height={120} />
                <p className="df-deposit-history-empty-copy">{emptyCopy(typeFilter)}</p>
                <div className="fiat-activity-empty-links">
                  <Link to="/asset_managemnet/deposit_fiat">Deposit Fiat</Link>
                  <Link to={convertDeepLink("buy", "USDT")}>Buy USDT</Link>
                  <Link to={convertDeepLink("sell", "USDT")}>Sell USDT</Link>
                  <Link to="/asset_managemnet/withdraw_fiat">Withdraw Fiat</Link>
                </div>
              </div>
            ) : null}

            {rows.length > 0 ? (
              <>
                <div className="df-deposit-history-desktop df-deposit-history-desktop--fiat fiat-activity-desktop">
                  <div className="df-deposit-history-head-wrap">
                    <div className="df-deposit-history-head" role="row">
                      {["Date", "Type", "Details", "Amount", "Status", ""].map((label) => (
                        <div key={label || "open"} className="df-deposit-history-head-cell" role="columnheader">
                          {label}
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="df-deposit-history-body-wrap">
                    {rows.map((row) => (
                      <div key={row.key} className="df-deposit-history-row" role="row">
                        <div className="df-deposit-history-row-cell">{row.dateLabel}</div>
                        <div className="df-deposit-history-row-cell">{row.typeLabel}</div>
                        <div className="df-deposit-history-row-cell">{row.detail}</div>
                        <div className="df-deposit-history-row-cell">
                          {row.amountLabel}
                          {row.type === "AED_WITHDRAW" && row.feeLabel ? (
                            <span className="df-deposit-history-reason">
                              Fee {row.feeLabel} · Net {row.netLabel}
                            </span>
                          ) : null}
                        </div>
                        <div className="df-deposit-history-row-cell df-deposit-history-row-cell--action">
                          <span className={`df-deposit-history-action-badge is-${row.statusTone}`}>{row.statusLabel}</span>
                          {row.statusReason ? <span className="df-deposit-history-reason">{row.statusReason}</span> : null}
                        </div>
                        <div className="df-deposit-history-row-cell">
                          <Link to={row.href} className="fiat-activity-open">
                            {row.actionLabel}
                          </Link>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="df-deposit-history-mobile" aria-label="Fiat activity">
                  {rows.map((row) => (
                    <div key={row.key} className="df-deposit-history-mobile-card">
                      <div className="df-deposit-history-mobile-top">
                        <span className="df-deposit-history-mobile-date">{row.dateLabel}</span>
                        <span className={`df-deposit-history-action-badge is-${row.statusTone}`}>{row.statusLabel}</span>
                      </div>
                      <div className="df-deposit-history-mobile-row">
                        <span className="df-deposit-history-mobile-label">Type</span>
                        <span className="df-deposit-history-mobile-value">{row.typeLabel}</span>
                      </div>
                      <div className="df-deposit-history-mobile-row">
                        <span className="df-deposit-history-mobile-label">Details</span>
                        <span className="df-deposit-history-mobile-value">{row.detail}</span>
                      </div>
                      <div className="df-deposit-history-mobile-row">
                        <span className="df-deposit-history-mobile-label">Amount</span>
                        <span className="df-deposit-history-mobile-value">{row.amountLabel}</span>
                      </div>
                      {row.type === "AED_WITHDRAW" && row.feeLabel ? (
                        <>
                          <div className="df-deposit-history-mobile-row">
                            <span className="df-deposit-history-mobile-label">Fee</span>
                            <span className="df-deposit-history-mobile-value">{row.feeLabel}</span>
                          </div>
                          <div className="df-deposit-history-mobile-row">
                            <span className="df-deposit-history-mobile-label">Net</span>
                            <span className="df-deposit-history-mobile-value">{row.netLabel}</span>
                          </div>
                        </>
                      ) : null}
                      {row.statusReason ? (
                        <div className="df-deposit-history-mobile-row">
                          <span className="df-deposit-history-mobile-label">Reason</span>
                          <span className="df-deposit-history-mobile-value">{row.statusReason}</span>
                        </div>
                      ) : null}
                      <div className="df-deposit-history-mobile-row">
                        <span className="df-deposit-history-mobile-label">Open</span>
                        <span className="df-deposit-history-mobile-value">
                          <Link to={row.href} className="fiat-activity-open">
                            {row.actionLabel}
                          </Link>
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : null}

            {hasMore && !error ? (
              <div className="df-deposit-history-more">
                <button
                  type="button"
                  className="df-deposit-history-export-btn"
                  onClick={() => loadPage(page + 1, false)}
                  disabled={loadingMore}
                >
                  {loadingMore ? "Loading…" : "Load more"}
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
}
